import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Loader2, Send, X } from 'lucide-react';
import { openapiAPI } from '../../utils/api';
import { OPENAPI_QUOTA_QUERY_KEY } from '../../hooks/useOpenapiQuota';

export type UpgradeMode = 'feature' | 'quota_advanced' | 'quota_visura';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  mode: UpgradeMode;
  // optional override per il default-fill della quota richiesta
  defaultQuotaAdvanced?: number;
  defaultQuotaVisura?: number;
}

const MODE_TITLES: Record<UpgradeMode, string> = {
  feature: 'Richiedi attivazione integrazione anagrafica imprese',
  quota_advanced: 'Richiedi aumento quota ricerca avanzata',
  quota_visura: 'Richiedi aumento quota visure camerali',
};

const MODE_DESCRIPTIONS: Record<UpgradeMode, string> = {
  feature:
    'Spiega all\'host perché ti serve l\'integrazione anagrafica imprese (validazione P.IVA, ricerca anagrafica, visure camerali). La richiesta verrà valutata dall\'amministratore.',
  quota_advanced:
    'La quota giornaliera per la ricerca anagrafica avanzata è esaurita. Spiega perché ti serve un upgrade.',
  quota_visura:
    'La quota mensile visure è esaurita. Spiega perché ti serve un upgrade.',
};

export default function RequestOpenapiUpgradeModal({
  isOpen,
  onClose,
  mode,
  defaultQuotaAdvanced,
  defaultQuotaVisura,
}: Props) {
  const queryClient = useQueryClient();
  const [motivazione, setMotivazione] = useState('');
  const [quotaAdvanced, setQuotaAdvanced] = useState<string>('');
  const [quotaVisura, setQuotaVisura] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;
    setMotivazione('');
    setQuotaAdvanced(
      mode === 'quota_advanced' && defaultQuotaAdvanced
        ? String(defaultQuotaAdvanced)
        : ''
    );
    setQuotaVisura(
      mode === 'quota_visura' && defaultQuotaVisura
        ? String(defaultQuotaVisura)
        : ''
    );
  }, [isOpen, mode, defaultQuotaAdvanced, defaultQuotaVisura]);

  const submitMutation = useMutation({
    mutationFn: () => {
      const advParsed = parseInt(quotaAdvanced, 10);
      const visParsed = parseInt(quotaVisura, 10);
      return openapiAPI.requestUpgrade({
        motivazione: motivazione.trim(),
        requested_quota_advanced_daily:
          mode === 'quota_advanced' && Number.isFinite(advParsed) && advParsed > 0
            ? advParsed
            : null,
        requested_quota_visura_monthly:
          mode === 'quota_visura' && Number.isFinite(visParsed) && visParsed > 0
            ? visParsed
            : null,
      });
    },
    onSuccess: () => {
      toast.success('Richiesta inviata all\'host');
      queryClient.invalidateQueries({ queryKey: OPENAPI_QUOTA_QUERY_KEY });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della richiesta');
    },
  });

  if (!isOpen) return null;

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const canSubmit = motivazione.trim().length >= 10 && !submitMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-lg font-semibold">{MODE_TITLES[mode]}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">{MODE_DESCRIPTIONS[mode]}</p>

          {mode === 'quota_advanced' && (
            <div>
              <label className={labelClass}>Quota richiesta (call/giorno)</label>
              <input
                type="number"
                min={1}
                value={quotaAdvanced}
                onChange={(e) => setQuotaAdvanced(e.target.value)}
                placeholder="es. 50"
                className={inputClass}
              />
            </div>
          )}

          {mode === 'quota_visura' && (
            <div>
              <label className={labelClass}>Quota richiesta (visure/mese)</label>
              <input
                type="number"
                min={1}
                value={quotaVisura}
                onChange={(e) => setQuotaVisura(e.target.value)}
                placeholder="es. 10"
                className={inputClass}
              />
            </div>
          )}

          <div>
            <label className={labelClass}>
              Motivazione <span className="text-red-500">*</span>
            </label>
            <textarea
              value={motivazione}
              onChange={(e) => setMotivazione(e.target.value)}
              rows={5}
              placeholder="Descrivi il caso d'uso (min 10 caratteri)"
              className={inputClass}
            />
            <p className="text-xs text-gray-500 mt-1">
              {motivazione.trim().length}/10 caratteri minimi
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => submitMutation.mutate()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            {submitMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Invia richiesta
          </button>
        </div>
      </div>
    </div>
  );
}
